import { useEffect, useRef } from 'react'
import { motion, useMotionValue, useMotionValueEvent, useTransform } from 'framer-motion'

const TYPING_SPEED_MS = 35
const DELETING_SPEED_MS = 20
const PAUSE_AFTER_TYPED_MS = 1800
const PAUSE_BEFORE_NEXT_MS = 300

export type TypewriterProps = {
  phrases: string[]
  className?: string
  /** Fires with the current typed text on every character change — for syncing plain-text targets (e.g. an `<input placeholder>`) that can't render a MotionValue directly. */
  onTextChange?: (text: string) => void
}

/**
 * Types `phrases` out one character at a time, holds it, then backspaces it
 * before moving to the next phrase (looping forever). The typed text is
 * driven by a framer-motion MotionValue rendered as `motion.span` children,
 * so the DOM text updates on every keystroke without triggering a React
 * re-render.
 */
export default function Typewriter({ phrases, className, onTextChange }: TypewriterProps) {
  const phraseIndexRef = useRef(0)
  const isDeletingRef = useRef(false)
  const charCount = useMotionValue(0)

  const text = useTransform(charCount, (latest) => {
    const phrase = phrases[phraseIndexRef.current % phrases.length] ?? ''
    return phrase.slice(0, latest)
  })

  useMotionValueEvent(text, 'change', (latest) => onTextChange?.(latest))

  useEffect(() => {
    if (phrases.length === 0) return

    let timeout: ReturnType<typeof setTimeout>

    const tick = () => {
      const phrase = phrases[phraseIndexRef.current % phrases.length]
      const count = charCount.get()

      if (!isDeletingRef.current && count >= phrase.length) {
        timeout = setTimeout(() => {
          isDeletingRef.current = true
          tick()
        }, PAUSE_AFTER_TYPED_MS)
        return
      }

      if (isDeletingRef.current && count <= 0) {
        timeout = setTimeout(() => {
          isDeletingRef.current = false
          phraseIndexRef.current = (phraseIndexRef.current + 1) % phrases.length
          tick()
        }, PAUSE_BEFORE_NEXT_MS)
        return
      }

      timeout = setTimeout(
        () => {
          charCount.set(count + (isDeletingRef.current ? -1 : 1))
          tick()
        },
        isDeletingRef.current ? DELETING_SPEED_MS : TYPING_SPEED_MS
      )
    }

    tick()
    return () => clearTimeout(timeout)
  }, [phrases, charCount])

  return <motion.span className={className}>{text}</motion.span>
}
